import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  ArrowLeft,
  RefreshCw,
  Share2
} from 'lucide-react';

const BusinessProfilePage = () => {
  const { businessId } = useParams();
  const [business, setBusiness] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bizRes, postsRes] = await Promise.all([
          axios.get(`${API}/businesses/${businessId}`),
          axios.get(`${API}/businesses/${businessId}/posts`)
        ]);
        setBusiness(bizRes.data);
        setPosts(postsRes.data);
      } catch (error) {
        console.error('Failed to fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [businessId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Business not found</p>
            <Link to="/directory">
              <Button variant="link" className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Directory
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const socialLinks = [
    { key: 'social_facebook', icon: Facebook, label: 'Facebook', color: 'bg-blue-600' },
    { key: 'social_instagram', icon: Instagram, label: 'Instagram', color: 'bg-pink-600' },
    { key: 'social_twitter', icon: Twitter, label: 'Twitter', color: 'bg-sky-500' },
    { key: 'social_linkedin', icon: Linkedin, label: 'LinkedIn', color: 'bg-blue-700' }
  ].filter(s => business[s.key]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Cover Image */}
      <div className="relative h-64 md:h-80">
        {business.cover_url ? (
          <img src={business.cover_url} alt={business.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-700" />
        )}
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Back Button */}
        <Link to="/directory" className="absolute top-4 left-4">
          <Button variant="secondary" className="bg-white/90 hover:bg-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10 pb-12">
        {/* Header Card */}
        <Card className="bg-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Logo */}
              <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-xl shadow-md flex items-center justify-center overflow-hidden flex-shrink-0 -mt-16 md:-mt-20 border-4 border-white">
                {business.logo_url ? (
                  <img src={business.logo_url} alt={business.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-12 h-12 text-slate-300" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="mb-2 capitalize">{business.category}</Badge>
                    {business.verified && <Badge className="ml-2 bg-emerald-500">Verified</Badge>}
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">{business.name}</h1>
                  </div>
                  <Button variant="outline" size="icon">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-slate-600 mt-2">{business.description}</p>
              </div>
            </div>

            {/* Quick Info */}
            <div className="mt-6 pt-6 border-t grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span className="text-sm text-slate-600">{business.address}</span>
              </div>
              {business.phone && (
                <a href={`tel:${business.phone}`} className="flex items-center gap-3 hover:text-emerald-600">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <span className="text-sm">{business.phone}</span>
                </a>
              )}
              {business.email && (
                <a href={`mailto:${business.email}`} className="flex items-center gap-3 hover:text-emerald-600">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-sm truncate">{business.email}</span>
                </a>
              )}
              {business.hours && (
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">{business.hours}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-3">
              {business.website && (
                <a href={business.website} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    <Globe className="w-4 h-4 mr-2" />
                    Visit Website
                  </Button>
                </a>
              )}
              {socialLinks.map((social) => (
                <a key={social.key} href={business[social.key]} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className={`${social.color} text-white border-0 hover:opacity-90`}>
                    <social.icon className="w-4 h-4 mr-2" />
                    {social.label}
                  </Button>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        {business.services?.length > 0 && (
          <Card className="bg-white mt-6">
            <CardHeader>
              <CardTitle className="font-heading">Services</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {business.services.map((service) => (
                  <Badge key={service} variant="secondary" className="px-3 py-1">{service}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Posts/Updates */}
        {posts.length > 0 && (
          <Card className="bg-white mt-6">
            <CardHeader>
              <CardTitle className="font-heading">Recent Updates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {posts.map((post) => (
                <div key={post.id} className="p-4 bg-slate-50 rounded-xl">
                  {post.image_url && (
                    <img src={post.image_url} alt="" className="w-full h-48 object-cover rounded-lg mb-3" />
                  )}
                  <p className="text-slate-700">{post.content}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(post.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BusinessProfilePage;
